// Escopo do chat (onde o chat processa: Community · uma pool dedicada · uma pool on-premise), compartilhado pela casca e pelo Chat.
// O escopo ATIVO é `me.scope` (guardado na conta pelo servidor: PUT /api/client/scope); as opções vêm de GET /api/client/scopes.
// Não confundir com o "tipo de consumo" do servidor (`tier`) nem com a faixa Small/Medium/High do Modo Turbo.
import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { api, ApiError, type ModelsResponse, type Scope, type ScopesResponse } from './api';
import { useMeRequired } from './me';
import { toast } from './components/toast';
import { LABELS } from './labels';

/** Nome na tela: Community vem de `labels.ts` (Q17); a pool dedicada / on-premise tem o nome que o gestor da plataforma deu. */
export const scopeName = (s: Pick<Scope, 'kind' | 'name'>) => (s.kind === 'community' ? LABELS.community : s.name);
/** 1 → "1×", 0,2 → "0,2×", 1,5 → "1,5×". */
export const fmtMult = (m: number) => `${(Number(m) || 0).toLocaleString('pt-BR')}×`;
/** Pool dedicada ou on-premise utilizável (o Modo Desenvolvedor só existe aqui). */
export const isSpecificScope = (s: Scope | null | undefined): s is Scope => Boolean(s && s.kind !== 'community' && s.available);

/** Linha provisória do escopo ativo quando a lista não carregou: o chat continua funcionando e o rótulo diz a verdade sobre o id. */
function synth(id: string): Scope {
  if (id.startsWith('pool:')) return { id, kind: 'dedicated', name: 'Pool dedicada', multiplier: 1, machines: 0, available: true };
  if (id !== 'community') return { id, kind: 'onprem', name: 'Escopo indisponível', multiplier: 1, machines: 0, available: false };   // escopo antigo guardado na conta: nunca se passa por Community
  return { id: 'community', kind: 'community', name: LABELS.community, multiplier: 1, machines: 0, available: true };
}

type ScopeState = {
  scopes: Scope[] | null;      // null = carregando (ou falhou: ver `failed`)
  failed: boolean;             // /scopes falhou: o controle cai para um rótulo estático com o escopo ativo
  active: string;              // id do escopo ativo
  current: Scope | null;       // a linha do escopo ativo (nula só enquanto carrega)
  select: (id: string) => Promise<boolean>;   // PUT /scope; erro vira toast e a lista recarrega
  reload: () => Promise<void>;
};

const Ctx = createContext<ScopeState | null>(null);

export function ScopeProvider({ children }: { children: ReactNode }) {
  const { me, patch } = useMeRequired();
  const [scopes, setScopes] = useState<Scope[] | null>(null);
  const [failed, setFailed] = useState(false);
  const meRef = useRef(me); meRef.current = me;
  const patchRef = useRef(patch); patchRef.current = patch;
  const gen = useRef(0);          // resposta de uma carga que já foi trocada por outra não mexe na tela
  const warned = useRef(false);   // o aviso de falha sai uma vez por sequência de falhas
  const active = me.scope ?? 'community';

  const reload = useCallback(async () => {
    const mine = ++gen.current;
    try {
      const r = await api<ScopesResponse>('/api/client/scopes');
      if (mine !== gen.current) return;
      setScopes(r.scopes); setFailed(false); warned.current = false;
      if (r.active !== meRef.current.scope) patchRef.current({ scope: r.active });   // trocado em outra aba
    } catch {
      if (mine !== gen.current) return;
      setFailed(true);
      if (!warned.current) { warned.current = true; toast('Não foi possível carregar as pools', 'err'); }
    }
  }, []);

  useEffect(() => { void reload(); const id = setInterval(() => { void reload(); }, 30000); return () => { gen.current++; clearInterval(id); }; }, [reload]);

  const select = useCallback(async (id: string) => {
    if (id === meRef.current.scope) return true;
    try {
      const r = await api<{ active: string }>('/api/client/scope', { method: 'PUT', body: JSON.stringify({ scope: id }) });
      patchRef.current({ scope: r.active });
      return true;
    } catch (e) {
      toast((e as Error).message, 'err');
      void reload();
      return false;
    }
  }, [reload]);

  const value = useMemo<ScopeState>(() => {
    const list = scopes ?? (failed ? [synth(active)] : null);
    return { scopes: list, failed, active, current: list ? list.find((s) => s.id === active) ?? synth(active) : null, select, reload };
  }, [scopes, failed, active, select, reload]);

  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

export function useScope(): ScopeState {
  const v = useContext(Ctx);
  if (!v) throw new Error('useScope fora do ScopeProvider');
  return v;
}

/**
 * Modelos de um escopo (GET /api/client/models?scope=): abertos, comerciais (só na Community), faixas do Modo Turbo e, com `detail`, o que
 * está instalado (Modo Desenvolvedor). Recarrega a cada 30 s e quando o escopo muda; `data` só vale para o escopo pedido (nunca a lista do anterior).
 * Se o servidor recusar o `detail` (403: a preferência mudou em outra aba), repete sem ele: o seletor continua com a lista comum.
 */
export type ScopeModels = { data: ModelsResponse | null; loading: boolean; error: string | null; reload: () => void };
export function useScopeModels(scope: string, detail = false): ScopeModels {
  const key = `${scope}|${detail ? 1 : 0}`;
  const [state, setState] = useState<{ key: string; data: ModelsResponse | null; error: string | null }>({ key: '', data: null, error: null });
  const gen = useRef(0);

  const load = useCallback(async () => {
    const mine = ++gen.current;
    const url = (d: boolean) => `/api/client/models?scope=${encodeURIComponent(scope)}${d ? '&detail=1' : ''}`;
    try {
      let data: ModelsResponse;
      try { data = await api<ModelsResponse>(url(detail)); }
      catch (e) { if (detail && e instanceof ApiError && e.status === 403) data = await api<ModelsResponse>(url(false)); else throw e; }
      if (mine === gen.current) setState({ key, data, error: null });
    } catch (e) {
      if (mine === gen.current) setState({ key, data: null, error: (e as Error).message });
    }
  }, [scope, detail, key]);

  useEffect(() => { void load(); const id = setInterval(() => { void load(); }, 30000); return () => { gen.current++; clearInterval(id); }; }, [load]);

  const fresh = state.key === key;
  return { data: fresh ? state.data : null, error: fresh ? state.error : null, loading: !fresh, reload: () => { void load(); } };
}
